/**
 * Utility for managing form navigation state in localStorage
 * Tracks current and previous step per application to enable:
 * - Navigation restoration after page refresh
 * - Validation triggering when navigating backwards (previousStep > currentStep)
 */

const NAVIGATION_STORAGE_KEY = 'application_navigation_state';

interface NavigationState {
  applicationId: string;
  currentStep: number;
  previousStep: number;
  lastUpdated: string;
}

interface NavigationStorageData {
  [applicationId: string]: NavigationState;
}

/**
 * Gets the navigation state for all applications
 */
const getStorageData = (): NavigationStorageData => {
  try {
    const data = localStorage.getItem(NAVIGATION_STORAGE_KEY);
    return data ? JSON.parse(data) : {};
  } catch (error) {
    console.error('Error reading navigation storage:', error);
    return {};
  }
};

/**
 * Saves the navigation state for all applications
 */
const setStorageData = (data: NavigationStorageData): void => {
  try {
    localStorage.setItem(NAVIGATION_STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving navigation storage:', error);
  }
};

/**
 * Gets the navigation state for a specific application
 */
export const getNavigationState = (
  applicationId: string
): NavigationState | null => {
  const data = getStorageData();
  return data[applicationId] || null;
};

/**
 * Saves the navigation state for a specific application
 */
export const saveNavigationState = (
  applicationId: string,
  newStep: number
): void => {
  const data = getStorageData();
  const existingState = data[applicationId];

  // The current step becomes the previous step
  const previousStep = existingState?.currentStep ?? newStep;

  data[applicationId] = {
    applicationId,
    currentStep: newStep,
    previousStep: previousStep,
    lastUpdated: new Date().toISOString(),
  };

  setStorageData(data);
};

/**
 * Checks if the user is navigating backwards
 * Returns true if the new step is less than the previous step (current step before navigation)
 */
export const isNavigatingBackwards = (
  applicationId: string,
  newStep: number
): boolean => {
  const state = getNavigationState(applicationId);
  if (!state) return false;

  // Navigating backwards means: newStep < currentStep (where currentStep is the step we're coming FROM)
  return newStep < state.currentStep;
};

/**
 * Clears navigation state for a specific application
 * Useful when an application is submitted or deleted
 */
export const clearNavigationState = (applicationId: string): void => {
  const data = getStorageData();
  delete data[applicationId];
  setStorageData(data);
};

/**
 * Clears all navigation states
 * Useful when user logs out or changes account
 */
export const clearAllNavigationStates = (): void => {
  try {
    localStorage.removeItem(NAVIGATION_STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing all navigation states:', error);
  }
};

/**
 * Clears old navigation states (older than 30 days)
 * Call this on app initialization to keep localStorage clean
 */
export const cleanupOldNavigationStates = (): void => {
  const data = getStorageData();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  let hasChanges = false;
  Object.keys(data).forEach((appId) => {
    const state = data[appId];
    const lastUpdated = new Date(state.lastUpdated);

    if (lastUpdated < thirtyDaysAgo) {
      delete data[appId];
      hasChanges = true;
    }
  });

  if (hasChanges) {
    setStorageData(data);
  }
};
